/**
 * D-041 · D-076: değişim kaleminin stok paketi listelerinde "olası eşleşme" kuralı. Kural yalnız burada durur: triyajın
 * "From stock" ve "Change stock package" listeleri (web) ile (c) çiftlerini kuran sunucu (GET /muayene/:rn) bu dosyayı çağırır.
 *
 * Saf fonksiyonlar: kural yalnız listeyi daraltır, sistem hiçbir paketi kendiliğinden seçmez (D-041). Bir stok paketi değişim
 * kalemi için olası eşleşmedir, eğer PN'i
 * (a) kalemin adında, bulgusunda, önerilen işinde ya da Order PN'inde BÜTÜN BİR PARÇA olarak geçiyorsa (büyük/küçük harf fark
 *     etmez; önünde ve ardında harf, rakam ya da tire olmaz: "56004001053" "…Snap Ring - 56004001053)" içinde tutar,
 *     "56004001053-2" içinde tutmaz);
 * (b) FM-0012'de basılı bir OEM PN'in yanındaki OSI PN'iyse (`FM12_SATIRLARI`, formun 19 basılı çifti) ve o OEM PN aynı
 *     metinlerde ya da kalemin MRB IDs alanında bütün bir parça olarak geçiyorsa;
 * (c) Order PN'i bu kalemle aynı olan BAŞKA bir değişim kaleminin (sınıfı "degisim", herhangi bir riser ve durum) adında,
 *     bulgusunda ya da önerilen işinde (a)'daki gibi geçiyorsa. Order PN'ler baştaki ve sondaki boşluk ile büyük/küçük harf
 *     dışında birebir karşılaştırılır; bileşik alan ("H139313-1H935 + H300384-52 …") yalnız birebir aynı alanla eşleşir.
 *     Başka kalemlerin stok seçimi (`stok_pn`, paket) okunmaz: yanlış bir eski seçim başka kaleme yayılmaz.
 * Kelime ya da tanım benzerliği, eş anlamlı listesi ve hat sözlüğü kullanılmaz (D-041'in parantez ayağı D-076 ile kalktı).
 * Kalemin bugünkü seçimi olası eşleşme sayılmaz. Gerekçe eşleşmeyi doğuran ilk yoldur: (a), (b), (c) sırasıyla.
 */
import { FM12_SATIRLARI } from "./fm12.js";
import { riserRnBicimi } from "./stok-paket.js";
/** (c) çiftinin taşıdığı en çok kaynak sayısı: kalem kendisini saymayınca bir sonraki kaynak gerekçe olur. */
export const CIFT_KAYNAK_SINIRI = 2;
const kacir = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/*
 * Desenlerde geriye bakış ("(?<!…)") yok: iOS/iPadOS 16.3 ve öncesi Safari onu tanımaz ve desen kurulurken hata atar,
 * bu da bütün "Waiting Items" penceresini düşürür. Önceki karakter tüketilerek denetlenir ("(?:^|[^…])"); yalnız
 * .test() kullanıldığı için sonuç aynıdır.
 */
const desenler = new Map();
/** Bir parçanın içinde geçtiği yerde önünde ve ardında harf, rakam ya da tire olmamalı ("g" bayrağı yok: .test() durumsuzdur). */
function parcaDeseni(s) {
    let d = desenler.get(s);
    if (!d) {
        d = new RegExp(`(?:^|[^A-Za-z0-9-])${kacir(s)}(?![A-Za-z0-9-])`, "i");
        desenler.set(s, d);
    }
    return d;
}
/** `parca` metinlerden birinde bütün bir parça olarak geçiyor mu (boş parça hiçbir yerde geçmez). */
function parcaGeciyor(parca, metinler) {
    const p = parca.trim();
    if (!p)
        return false;
    const d = parcaDeseni(p);
    return metinler.some((m) => Boolean(m) && d.test(m));
}
const ayniPn = (a, b) => a.trim().toUpperCase() === b.trim().toUpperCase();
/** Order PN'in karşılaştırma anahtarı: baştaki ve sondaki boşluk atılır, büyük harf; boşsa "". İçteki yazım aynen kalır. */
export const siparisPnAnahtari = (s) => (s ?? "").trim().toUpperCase();
/** FM-0012'nin OEM PN'i ve OSI PN'i birlikte basılı satırları (19 çift). */
export const FM12_PN_CIFTLERI = FM12_SATIRLARI.flatMap((s) => s.oemPn && s.osiPn ? [{ oemPn: s.oemPn, osiPn: s.osiPn }] : []);
/** PN bu kalem için olası eşleşme mi; öyleyse ilk yolun gerekçesi, değilse null. `ciftler` (c) içindir (sunucudan gelir). */
export function olasiEslesmeGerekcesi(kalem, pn, ciftler = []) {
    const p = pn.trim();
    if (!p)
        return null;
    const metinler = [kalem.kalem, kalem.bulgu, kalem.onerilenIs, kalem.siparisPn];
    // (a) kalemin kendi metni
    if (parcaGeciyor(p, metinler))
        return { yol: "metin", pn: p };
    // (b) FM-0012'nin basılı çifti: OEM PN kalemin metninde ya da MRB IDs alanında
    const fm = FM12_PN_CIFTLERI.find((c) => ayniPn(c.osiPn, p) && parcaGeciyor(c.oemPn, [...metinler, kalem.mrbKimlikler]));
    if (fm)
        return { yol: "fm12", oemPn: fm.oemPn, osiPn: fm.osiPn };
    // (c) aynı Order PN'li başka bir değişim kaleminin OSI metni; kalem kendisini kaynak saymaz
    const anahtar = siparisPnAnahtari(kalem.siparisPn);
    if (!anahtar)
        return null;
    for (const c of ciftler) {
        if (siparisPnAnahtari(c.siparisPn) !== anahtar || !ayniPn(c.pn, p))
            continue;
        const kaynak = c.kaynaklar.find((k) => !kalem.id || k.kalemId !== kalem.id);
        if (kaynak)
            return { yol: "kalem", pn: p, siparisPn: (kalem.siparisPn ?? "").trim(), kaynak: kaynak.etiket };
    }
    return null;
}
/** PN sırası: düz karakter sırası (dile göre sıralama tireyi ve harfleri farklı dizebilir). Aynı PN'de girdi sırası korunur. */
const pnSirasi = (a, b) => (a.pn < b.pn ? -1 : a.pn > b.pn ? 1 : 0);
/**
 * Listenin iki grubu: olası eşleşenler ve ötekiler, ikisi de PN sırasıyla (aynı PN'in paketleri sunucu sırasında: önce açılış
 * paketi, sonra geliş tarihi). `gerekceler` olası eşleşen her PN'in gerekçesidir (anahtar paketin PN'i, yazıldığı gibi).
 */
export function stokSecenekleri(kalem, paketler, ciftler = []) {
    const gerekceler = new Map();
    const bakildi = new Set();
    const olasi = [];
    const diger = [];
    for (const p of [...paketler].sort(pnSirasi)) {
        if (!bakildi.has(p.pn)) {
            bakildi.add(p.pn);
            const g = olasiEslesmeGerekcesi(kalem, p.pn, ciftler);
            if (g)
                gerekceler.set(p.pn, g);
        }
        (gerekceler.has(p.pn) ? olasi : diger).push(p);
    }
    return { olasi, diger, gerekceler };
}
/** Riser sırası: "R" önekli numaralar sayıca, öteki yazımlar düz karakter sırasıyla sonda. */
function rnSirasi(a, b) {
    const sayi = (rn) => {
        const m = /^R(\d+)$/i.exec(rn);
        return m ? Number(m[1]) : null;
    };
    const x = sayi(a);
    const y = sayi(b);
    if (x !== null && y !== null && x !== y)
        return x - y;
    if (x !== null && y === null)
        return -1;
    if (x === null && y !== null)
        return 1;
    return a < b ? -1 : a > b ? 1 : 0;
}
const metinSirasi = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
/**
 * (c) çiftleri: her değişim kaleminin (sınıfı "degisim", Order PN'i dolu) adında, bulgusunda ya da önerilen işinde bütün bir
 * parça olarak geçen paket PN'leri, Order PN anahtarıyla. `pnler` paket PN'leridir (aktif ve biten); kalemin stok seçimi
 * okunmaz. `siparisPnleri` verilirse yalnız bu Order PN'lerin çiftleri döner (sunucu: yalnız açılan riserin Order PN'leri).
 * Sıra: Order PN anahtarı, sonra PN (düz karakter sırası).
 */
export function stokPnCiftleri(kalemler, pnler, siparisPnleri) {
    const istenen = siparisPnleri ? new Set([...siparisPnleri].map(siparisPnAnahtari).filter(Boolean)) : null;
    const pnListesi = [...new Set(pnler.map((p) => p.trim()).filter(Boolean))];
    const kume = new Map();
    for (const k of kalemler) {
        if (k.sinif !== "degisim")
            continue;
        const anahtar = siparisPnAnahtari(k.siparisPn);
        if (!anahtar || (istenen && !istenen.has(anahtar)))
            continue;
        const metinler = [k.kalem, k.bulgu, k.onerilenIs];
        const rn = riserRnBicimi(k.rn.trim());
        for (const pn of pnListesi) {
            if (!parcaGeciyor(pn, metinler))
                continue;
            const a = `${anahtar}\u0000${pn}`;
            const c = kume.get(a) ?? { siparisPn: anahtar, pn, kaynaklar: [] };
            c.kaynaklar.push({ kalemId: k.id, etiket: `${rn} ${k.komponent} #${k.no}`, rn, komponentSira: k.komponentSira, no: k.no });
            kume.set(a, c);
        }
    }
    return [...kume.values()]
        .sort((a, b) => metinSirasi(a.siparisPn, b.siparisPn) || metinSirasi(a.pn, b.pn))
        .map((c) => ({
        siparisPn: c.siparisPn,
        pn: c.pn,
        kaynaklar: c.kaynaklar
            .sort((x, y) => rnSirasi(x.rn, y.rn) || x.komponentSira - y.komponentSira || x.no - y.no || metinSirasi(x.kalemId, y.kalemId))
            .slice(0, CIFT_KAYNAK_SINIRI)
            .map(({ kalemId, etiket }) => ({ kalemId, etiket })),
    }));
}
//# sourceMappingURL=stok-eslesme.js.map